
import React from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../components/Footer";
import { Mail, Phone } from "lucide-react";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";

const sectionBackgrounds = [
  "bg-white",
  "bg-gray-100", 
  "bg-white",
  "bg-gray-100"
];

const sections = [
  {
    title: "New Clients & General Inquiry",
    helper: "If you are a new client wanting to use our Traffic Control Services or want to learn more about what we offer please reach out to us to discuss your specific project details and needs.",
    contact: true,
    contactInfo: {
      email: "Hello@USATrafficControl.com",
      phone: "720-298-9505"
    }
  },
  {
    title: "Request a Traffic Control Plan",
    helper: "If you are requesting a Traffic Control Plan please reach out to us by phone or email, if you are calling we will get the needed project information from you on the call, if you are emailing please include the following project info in your email:",
    bulletPoints: [
      "Your Company Name",
      "Expected Duration",
      "Project Address", 
      "Scope of Work"
    ],
    contact: true
  },
  {
    title: "Schedule Traffic Control Services",
    helper: "Contact us via phone or email to schedule Traffic Control Services or make changes to already scheduled services. You will receive an email from our Scheduling Department once you have been scheduled confirming your booking and start times. Please book your service as many days ahead as possible.",
    additionalInfo: [
      "Please include the following information in your email."
    ],
    bulletPoints: [
      "Traffic Control Plan (if you have one)",
      "Expected number of work days",
      "Overnight (yes or no)",
      "Worksite Address",
      "Start Time",
      "Start Date"
    ],
    contact: true,
    contactInfo: {
      email: "Schedule@USATrafficControl.com",
      phone: "720-298-9505"
    }
  },
  {
    title: "Billing Department",
    helper: "For any questions or inquiries related to your invoices or account please reach out to our Billing Team via phone or email.",
    contact: true,
    contactInfo: {
      email: "Billing@USATrafficControl.com",
      phone: "720-298-9505"
    }
  }
];

const Contact = () => {
  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Helmet>
        <title>Contact USA Traffic Control | Get In Touch</title>
        <meta name="description" content="Contact USA Traffic Control for professional traffic management services. Reach out for quotes, scheduling, or general inquiries about our traffic control solutions." />
        <meta name="keywords" content="contact traffic control, traffic management services, traffic control quote" />
        <link rel="canonical" href="https://usatrafficcontrol.com/contact" />
      </Helmet>
      
      <div className="relative w-full h-[600px] overflow-hidden">
        <div className="absolute inset-0 flex flex-col justify-end pb-16 z-10">
          <div className="w-full flex">
            <div className="w-[90%] md:w-[85%] lg:w-[80%] xl:w-[75%] text-right p-4 md:p-6 bg-orange-400">
              <div className="ml-auto w-[55%] text-left">
                <div className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-[2.8rem] leading-none font-bold text-white">
                  Get In Touch
                </div>
                <div className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-[2.8rem] leading-none font-bold text-white mt-2">
                  We're Here to Help
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="absolute w-full h-full">
          <img
            src="/lovable-uploads/7c62dd0b-e539-4203-a60a-6bc7f311869f.png"
            alt="Contact USA Traffic Control"
            className="w-full h-full object-cover"
            style={{ objectPosition: '50% 0%' }}
          />
        </div>
      </div>

      <main className="flex-grow">
        {sections.map((section, idx) => (
          <section
            key={section.title}
            className={`${sectionBackgrounds[idx % sectionBackgrounds.length]} py-16 md:py-20`}
          >
            <div className="container mx-auto px-4 max-w-5xl">
              <h2 className="text-3xl font-bold mb-4 text-left text-gray-900">{section.title}</h2>
              <p className="text-gray-700 text-lg mb-0 text-left">{section.helper}</p>
              
              {section.additionalInfo && section.additionalInfo.map((paragraph, index) => (
                <p key={index} className="text-gray-700 text-lg mt-4 text-left">{paragraph}</p>
              ))}
              
              {section.bulletPoints && (
                <ul className="list-disc ml-6 mt-4 mb-6 text-gray-700 text-lg">
                  {section.bulletPoints.map((point, index) => (
                    <li key={index}>{point}</li>
                  ))}
                </ul>
              )}
              {section.contact && (
                <div className="mt-6 space-y-3">
                  {section.contactInfo?.email && (
                    <div className="flex items-center gap-2 text-gray-700">
                      <Mail size={20} className="text-orange-500" />
                      <span>{section.contactInfo.email}</span>
                    </div>
                  )}
                  {section.contactInfo?.phone && (
                    <div className="flex items-center gap-2 text-gray-700">
                      <Phone size={20} className="text-orange-500" />
                      <span>{section.contactInfo.phone}</span>
                    </div>
                  )}
                  {!section.contactInfo && (
                    <>
                      <div className="flex items-center gap-2 text-gray-700">
                        <Mail size={20} className="text-orange-500" />
                        <span>Plans@USATrafficControl.com</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-700">
                        <Phone size={20} className="text-orange-500" />
                        <span>720-298-9505</span>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </section>
        ))}
      </main>

      <Footer />
    </div>
  );
};

export default Contact;
